import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

// CONFIRM DIALOG — in-dashboard, replacing window.confirm().
//
// WHY. The browser dialog says "app.voice.relaywork.co says", renders in Chrome's
// chrome rather than ours, cannot show structure, and on a shared screen looks like
// the site is throwing an error rather than asking a question. Brian asked for these
// to live in the dashboard.
//
// ⛔ IT MUST STILL BE A REAL GATE. window.confirm blocks the thread; this cannot, so
// every caller becomes async and awaits the answer. The one thing that must never
// happen is a "confirm" that resolves true by default — these guard re-dialling real
// businesses and deleting contacts. Dismissing (Escape, backdrop, Cancel) always
// resolves FALSE.
export default function ConfirmDialog({ open, title, body, confirmLabel = 'Confirm', danger = false, onResolve }) {
  const confirmRef = useRef(null);

  // Escape cancels. Bound while open only, so it can't swallow Escape elsewhere.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onResolve(false); }
      if (e.key === 'Enter') { e.preventDefault(); onResolve(true); }
    };
    window.addEventListener('keydown', onKey);
    // Focus the action so the dialog is keyboard-usable immediately.
    const t = setTimeout(() => confirmRef.current?.focus(), 0);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open, onResolve]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      // Backdrop click = cancel. Guarded on target so a click inside the card
      // (including a drag that ends outside) doesn't dismiss it.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onResolve(false); }}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="card w-full max-w-lg border-stone-700 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          {danger && <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-300" />}
          <div className="min-w-0 flex-1">
            <h2 id="confirm-title" className="text-lg text-stone-100">{title}</h2>
            {/* Body is pre-wrapped so callers can pass the same multi-line copy they
                used with window.confirm without it collapsing into one paragraph. */}
            {body && (
              <p className="mt-2 whitespace-pre-line text-sm text-stone-400">{body}</p>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => onResolve(false)}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={danger
              ? 'btn-ghost border-red-500/50 text-red-200 hover:border-red-400 hover:text-red-100'
              : 'btn-primary'}
            onClick={() => onResolve(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook giving an async `confirm(...)` that resolves to a boolean — a drop-in for
 * `window.confirm` at every call site, minus the browser chrome.
 *
 * Usage:
 *   const { confirm, dialogProps } = useConfirm();
 *   if (!(await confirm({ title, body, confirmLabel, danger }))) return;
 *   …
 *   <ConfirmDialog {...dialogProps} />
 */
export function useConfirm() {
  const resolverRef = useRef(null);
  const [state, setState] = useConfirmState();

  const confirm = (opts) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState({ open: true, ...opts });
  });

  const onResolve = (answer) => {
    setState((s) => ({ ...s, open: false }));
    const r = resolverRef.current;
    resolverRef.current = null;
    // Resolve AFTER clearing the ref so a caller that immediately opens another
    // dialog can't be answered by this one's resolver.
    if (r) r(answer);
  };

  return { confirm, dialogProps: { ...state, onResolve } };
}

// Tiny local state helper so the hook above stays readable.
function useConfirmState() {
  return useState({ open: false, title: '', body: '', confirmLabel: 'Confirm', danger: false });
}
